import React, { useLayoutEffect, useRef, useState } from "react";
import { Avatar, Box, Typography, TextField, IconButton, Button, Stack, Paper, Chip, CircularProgress, Tooltip, Alert } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DoneIcon from "@mui/icons-material/Done";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ImageZoom from "react-medium-image-zoom";
import LinkPreview, { extractUrls } from "../LinkPreview";
import Global from "../../customlib/Global";
import { getAvatarColor, getInitials, getLabelColor, getAttachmentKind } from "./livechatDisplayUtils";
import { LIVECHAT_MAX_UPLOAD_SIZE_MB } from "./livechatConstants";
import { PASTEL } from "./livechatTheme";

const LIVECHAT_MAX_UPLOAD_SIZE_BYTES = LIVECHAT_MAX_UPLOAD_SIZE_MB * 1024 * 1024;

export default function ChatPanel({
    session, messages, hasMoreMessages, loadingOlderMessages, onLoadOlderMessages,
    onSendMessage, onSendAttachment, onRetryMessage, onCloseSession, onOpenLabelDialog, isMobile, onBack,
    showContextPanelToggle, contextPanelOpen, onToggleContextPanel,
}) {
    const [text, setText] = useState("");
    const [pendingAttachment, setPendingAttachment] = useState(null);
    const [attachmentCaption, setAttachmentCaption] = useState("");
    const [attachmentError, setAttachmentError] = useState(null);
    const bottomRef = useRef(null);
    const fileInputRef = useRef(null);
    const scrollBoxRef = useRef(null);
    const prevMessagesRef = useRef(messages);
    const prevScrollHeightRef = useRef(0);

    useLayoutEffect(() => {
        const prevMessages = prevMessagesRef.current;
        const el = scrollBoxRef.current;
        // "Prepend" (halaman pesan lebih lama dimuat via scroll-ke-atas) dideteksi dari data,
        // bukan flag imperatif — pesan TERAKHIR tetap sama tapi pesan PERTAMA berubah & array
        // bertambah panjang di depan. Robust terhadap kegagalan fetch (tidak ada flag yang bisa
        // nyangkut kalau messages ternyata tidak berubah).
        const isPrepend =
            el && prevMessages.length > 0 && messages.length > prevMessages.length &&
            messages[0]?._id !== prevMessages[0]?._id &&
            messages[messages.length - 1]?._id === prevMessages[prevMessages.length - 1]?._id;

        if (isPrepend) {
            // Pertahankan posisi scroll visual (bukan lompat ke atas) dgn menggeser scrollTop
            // sebesar pertambahan tinggi konten. prevScrollHeightRef diisi di handleScroll,
            // SEBELUM state messages di-update oleh parent (lihat komentar di sana).
            el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
        } else {
            bottomRef.current?.scrollIntoView({ behavior: "smooth" });
        }
        prevMessagesRef.current = messages;
    }, [messages]);

    const handleScroll = () => {
        const el = scrollBoxRef.current;
        if (!el || !hasMoreMessages || loadingOlderMessages) return;
        if (el.scrollTop < 80) {
            // Dicatat SEBELUM memicu fetch (state React & network keduanya async) supaya nilai
            // scrollHeight yang dipakai di useLayoutEffect di atas masih yang "lama" (sebelum
            // pesan lama di-prepend), bukan yang sudah ikut bertambah.
            prevScrollHeightRef.current = el.scrollHeight;
            onLoadOlderMessages();
        }
    };

    if (!session) {
        return (
            <Box sx={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                gap: 1, backgroundColor: PASTEL.background,
            }}>
                <ChatBubbleOutlineIcon sx={{ fontSize: 56, color: "text.disabled" }} />
                <Typography color="text.secondary">Pilih percakapan di sebelah kiri untuk mulai membalas</Typography>
            </Box>
        );
    }

    const handleSend = () => {
        if (!text.trim()) return;
        onSendMessage(text.trim());
        setText("");
    };

    // Cek ukuran di client dulu (Asumsi #3 PRD DEV-1190, ~25MB) supaya user tidak perlu menunggu
    // upload gagal dulu baru tahu filenya kelewat besar — backend tetap yang menegakkan batas
    // sesungguhnya (livechatUpload.middleware.ts), ini murni UX cepat.
    const setPickedFile = (file) => {
        if (!file) return;
        if (file.size > LIVECHAT_MAX_UPLOAD_SIZE_BYTES) {
            setAttachmentError(`Ukuran file melebihi batas maksimal ${LIVECHAT_MAX_UPLOAD_SIZE_MB}MB`);
            return;
        }
        setAttachmentError(null);
        if (pendingAttachment) URL.revokeObjectURL(pendingAttachment.previewUrl);
        setPendingAttachment({ file, previewUrl: URL.createObjectURL(file), kind: getAttachmentKind(file.type) });
        setAttachmentCaption("");
    };

    const handlePickAttachment = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        setPickedFile(file);
    };

    // Paste gambar dari clipboard (mis. screenshot) langsung dari kotak ketik balasan, tanpa
    // perlu browse file — dipasang di TextField komposer via onPaste (diteruskan MUI ke
    // <textarea> asli, pola yang sama dengan onKeyDown di bawah).
    const handlePastePhoto = (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        const imageItem = Array.from(items).find((item) => item.type.startsWith("image/"));
        if (!imageItem) return;
        e.preventDefault();
        setPickedFile(imageItem.getAsFile());
    };

    const handleCancelAttachment = () => {
        if (!pendingAttachment) return;
        URL.revokeObjectURL(pendingAttachment.previewUrl);
        setPendingAttachment(null);
        setAttachmentCaption("");
    };

    // Kompresi + upload + kirim sekarang sepenuhnya jadi tanggung jawab onSendAttachment
    // (index.jsx), yang membuat bubble optimistic "sending" di daftar pesan utama begitu
    // dipanggil — supaya kegagalan (kompresi/upload/kirim) tampil sebagai status "Gagal
    // terkirim" + tombol retry di bubble itu sendiri, bukan lewat try-catch lokal di sini.
    const handleSubmitAttachment = () => {
        if (!pendingAttachment) return;
        const { file, previewUrl } = pendingAttachment;
        const caption = attachmentCaption.trim();
        setPendingAttachment(null);
        setAttachmentCaption("");
        onSendAttachment(file, caption, previewUrl);
    };

    const labelColor = getLabelColor(session.label?.labelType);
    const isActive = session.status === "active";

    return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Box sx={{
                px: { xs: 1.5, sm: 2.5 }, py: 1.5, borderBottom: `1px solid ${PASTEL.border}`, backgroundColor: PASTEL.surface,
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: { xs: 1, sm: 2 },
            }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                    {isMobile && (
                        <IconButton size="small" onClick={onBack} sx={{ flexShrink: 0, ml: -0.5 }}>
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    )}
                    <Avatar sx={{ bgcolor: getAvatarColor(session.customerName), width: 40, height: 40, fontSize: 15, flexShrink: 0 }}>
                        {getInitials(session.customerName)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2} noWrap>
                                {session.customerName}
                            </Typography>
                            {session.label?.labelType && (
                                <Chip
                                    size="small"
                                    label={session.label.labelType}
                                    sx={{
                                        height: 20, fontSize: 11, flexShrink: 0,
                                        backgroundColor: labelColor ? `${labelColor}22` : undefined,
                                        color: labelColor || undefined,
                                        border: labelColor ? `1px solid ${labelColor}55` : undefined,
                                    }}
                                />
                            )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {session.customerPhone} · {session.brand}
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                    {!isMobile && (
                        <Chip
                            size="small"
                            label={isActive ? "Aktif" : "Selesai"}
                            variant={isActive ? "filled" : "outlined"}
                            sx={isActive
                                ? { fontWeight: 600, backgroundColor: PASTEL.primary, color: "#fff" }
                                : { fontWeight: 600 }}
                        />
                    )}
                    {showContextPanelToggle && (
                        <Tooltip title={contextPanelOpen ? "Sembunyikan info pelanggan" : "Tampilkan info pelanggan"}>
                            <Button
                                size="small"
                                variant={contextPanelOpen ? "contained" : "outlined"}
                                onClick={onToggleContextPanel}
                                startIcon={<InfoOutlinedIcon fontSize="small" />}
                                sx={contextPanelOpen ? {
                                    textTransform: "none",
                                    minWidth: 0,
                                    px: 2,
                                    backgroundColor: PASTEL.primary,
                                    "&:hover": { backgroundColor: PASTEL.primaryDark },
                                } : {
                                    textTransform: "none",
                                    minWidth: 0,
                                    px: 2,
                                    color: PASTEL.primary,
                                    borderColor: PASTEL.primary,
                                    "&:hover": { borderColor: PASTEL.primaryDark, backgroundColor: PASTEL.primarySoft },
                                }}
                            >
                                Info
                            </Button>
                        </Tooltip>
                    )}
                    <Button
                        size="small" variant="outlined" onClick={onOpenLabelDialog}
                        startIcon={<LabelOutlinedIcon fontSize="small" />}
                        sx={{
                            textTransform: "none",
                            minWidth: 0,
                            px: isMobile ? 1 : 2,
                            color: PASTEL.primary,
                            borderColor: PASTEL.primary,
                            "&:hover": { borderColor: PASTEL.primaryDark, backgroundColor: PASTEL.primarySoft },
                            "& .MuiButton-startIcon": { mr: isMobile ? 0 : 1 },
                        }}
                    >
                        {!isMobile && "Set Label"}
                    </Button>
                    {isActive && (
                        <Button
                            size="small" variant="contained" onClick={onCloseSession}
                            startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                            sx={{
                                textTransform: "none",
                                minWidth: 0,
                                px: isMobile ? 1 : 2,
                                backgroundColor: PASTEL.primary,
                                "&:hover": { backgroundColor: PASTEL.primaryDark },
                                "& .MuiButton-startIcon": { mr: isMobile ? 0 : 1 },
                            }}
                        >
                            {!isMobile && "Selesai"}
                        </Button>
                    )}
                </Stack>
            </Box>

            <Box ref={scrollBoxRef} onScroll={handleScroll} 
                        onPaste={handlePastePhoto} sx={{ flex: 1, overflowY: "auto", p: { xs: 1.5, sm: 2 }, backgroundColor: PASTEL.background }}>
                {loadingOlderMessages && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                        <CircularProgress size={18} sx={{ color: PASTEL.primary }} />
                    </Box>
                )}
                {messages.map((m) => {
                    // "purchasing" = staff channel mitra-purchasing (DEV-1190/1193), sejajar "agent".
                    // "system" = sambutan otomatis (chatGreeting) — representasi pesan dari sisi kita
                    // (purchasing/agent), jadi sejajar kanan juga, bukan kiri seperti pesan customer/mitra.
                    const isAgent = m.sender === "agent" || m.sender === "purchasing" || m.sender === "system";
                    const urls = extractUrls(m.text);
                    return (
                        <Box key={m._id} sx={{ display: "flex", justifyContent: isAgent ? "flex-end" : "flex-start", mb: 1 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 0.7, maxWidth: { xs: "85%", sm: "70%" },
                                    backgroundColor: isAgent ? PASTEL.primary : PASTEL.surface,
                                    color: isAgent ? "#fff" : "text.primary",
                                    borderRadius: 2,
                                    borderBottomRightRadius: isAgent ? 4 : 16,
                                    borderBottomLeftRadius: isAgent ? 16 : 4,
                                    boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
                                }}
                            >
                                {m.attachmentUrl && (() => {
                                    // Pesan lama (sebelum field attachmentType ada) selalu berupa foto —
                                    // fallback ke "image" kalau attachmentType kosong (kompatibilitas mundur).
                                    const attachmentType = m.attachmentType || "image";
                                    if (attachmentType === "video") {
                                        return (
                                            <Box sx={{ mb: 0.5, lineHeight: 0 }}>
                                                <Box
                                                    component="video"
                                                    src={m.attachmentUrl}
                                                    controls
                                                    sx={{ width: 280, maxWidth: "100%", maxHeight: 320, borderRadius: 1, display: "block" }}
                                                />
                                            </Box>
                                        );
                                    }
                                    if (attachmentType === "document") {
                                        return (
                                            <Box
                                                component="a"
                                                href={m.attachmentUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    display: "flex", alignItems: "center", gap: 1, mb: 0.5, p: 1,
                                                    borderRadius: 1.5, textDecoration: "none", minWidth: 180,
                                                    backgroundColor: isAgent ? "rgba(255,255,255,0.15)" : PASTEL.surfaceAlt,
                                                    border: isAgent ? "1px solid rgba(255,255,255,0.3)" : `1px solid ${PASTEL.border}`,
                                                }}
                                            >
                                                <PictureAsPdfRoundedIcon sx={{ color: isAgent ? "#fff" : PASTEL.primary }} />
                                                <Typography variant="body2" sx={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                                                    {m.attachmentName || "Dokumen"}
                                                </Typography>
                                                <DownloadRoundedIcon fontSize="small" sx={{ color: isAgent ? "#fff" : "text.secondary" }} />
                                            </Box>
                                        );
                                    }
                                    return (
                                        <Box sx={{ mb: 0.5, lineHeight: 0 }}>
                                            <ImageZoom
                                                image={{
                                                    src: m.attachmentUrl,
                                                    alt: "lampiran",
                                                    style: {
                                                        maxWidth: 180,
                                                        width: "auto",
                                                        height: "auto",
                                                        maxHeight: 140,
                                                        borderRadius: 8,
                                                        display: "block",
                                                        objectFit: "contain",
                                                    },
                                                }}
                                                zoomImage={{ src: m.attachmentUrl, alt: "lampiran" }}
                                            />
                                        </Box>
                                    );
                                })()}
                                {m.text && <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{m.text}</Typography>}
                                {urls.length > 0 && <LinkPreview key={urls[0]} url={urls[0]} />}
                                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" sx={{ mt: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: isAgent ? "rgba(255,255,255,0.75)" : "text.secondary" }}>
                                        {Global.formatDateTimeToStrTime24(m.created_at, true)}
                                    </Typography>
                                    {isAgent && (
                                        m.deliveryStatus === "failed" ? (
                                            <>
                                                <ErrorOutlineIcon sx={{ fontSize: 13, color: "#ffd2e0" }} />
                                                <Typography variant="caption" sx={{ fontSize: 11, color: "#ffd2e0" }}>Gagal terkirim</Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onRetryMessage(m)}
                                                    aria-label="Kirim ulang"
                                                    sx={{ p: 0.25, color: "#ffd2e0", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}
                                                >
                                                    <ReplayIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </>
                                        ) : m.deliveryStatus === "sending" ? (
                                            <AccessTimeIcon sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)" }} />
                                        ) : m.readByCustomerAt ? (
                                            // Biru (#8ecbff) menandai "sudah dibaca", konsisten dengan centang di
                                            // livechat-customer-fe/ChatScreen.jsx — bukan cuma beda bentuk ikon.
                                            <DoneAllIcon sx={{ fontSize: 14, color: "#8ecbff" }} />
                                        ) : (
                                            <DoneIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.75)" }} />
                                        )
                                    )}
                                </Stack>
                            </Paper>
                        </Box>
                    );
                })}
                <div ref={bottomRef} />
            </Box>

            {attachmentError && (
                <Alert severity="error" sx={{ mx: { xs: 1, sm: 2 }, mt: 1 }} onClose={() => setAttachmentError(null)}>
                    {attachmentError}
                </Alert>
            )}

            <input
                type="file" accept="image/*,video/*,application/pdf" hidden
                ref={fileInputRef} onChange={handlePickAttachment}
            />

            {pendingAttachment ? (
                <Box sx={{
                    px: { xs: 1, sm: 2 }, py: 1.5, borderTop: `1px solid ${PASTEL.border}`, backgroundColor: PASTEL.surfaceAlt,
                }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", letterSpacing: 0.2 }}>
                            {pendingAttachment.kind === "image" ? "Pratinjau foto"
                                : pendingAttachment.kind === "video" ? "Pratinjau video" : "Pratinjau dokumen"}
                        </Typography>
                        <IconButton size="small" onClick={handleCancelAttachment}>
                            <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {pendingAttachment.kind === "image" ? (
                            <Box
                                component="img"
                                src={pendingAttachment.previewUrl}
                                alt="preview"
                                sx={{
                                    width: 56, height: 56, borderRadius: 1.5, objectFit: "cover",
                                    flexShrink: 0, border: `1px solid ${PASTEL.border}`, backgroundColor: PASTEL.surface,
                                }}
                            />
                        ) : (
                            <Box sx={{
                                width: 56, height: 56, borderRadius: 1.5, flexShrink: 0, display: "flex",
                                alignItems: "center", justifyContent: "center",
                                border: `1px solid ${PASTEL.border}`, backgroundColor: PASTEL.surface,
                            }}>
                                {pendingAttachment.kind === "video"
                                    ? <VideocamRoundedIcon sx={{ color: PASTEL.primary }} />
                                    : <PictureAsPdfRoundedIcon sx={{ color: PASTEL.primary }} />}
                            </Box>
                        )}
                        <TextField
                            fullWidth size="small" placeholder="Tambahkan keterangan (opsional)..."
                            value={attachmentCaption}
                            onChange={(e) => setAttachmentCaption(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmitAttachment(); } }}
                            InputProps={{ sx: { borderRadius: 3, backgroundColor: PASTEL.surface } }}
                        />
                        <IconButton
                            onClick={handleSubmitAttachment}
                            sx={{
                                flexShrink: 0, width: 40, height: 40,
                                backgroundColor: PASTEL.primary, color: "#fff",
                                "&:hover": { backgroundColor: PASTEL.primaryDark },
                            }}
                        >
                            <SendIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            ) : (
                <Box sx={{
                    px: { xs: 1, sm: 2 }, py: 1.5, borderTop: `1px solid ${PASTEL.border}`, backgroundColor: PASTEL.surface,
                    display: "flex", alignItems: "flex-end", gap: 1, flexShrink: 0,
                }}>
                    <IconButton onClick={() => fileInputRef.current?.click()} sx={{ color: "text.secondary", flexShrink: 0 }}>
                        <AttachFileIcon />
                    </IconButton>
                    <TextField
                        fullWidth size="small" placeholder="Tulis balasan..."
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                        onPaste={handlePastePhoto}
                        multiline
                        maxRows={4}
                        InputProps={{ sx: { borderRadius: 3, backgroundColor: PASTEL.surfaceAlt, py: 1 } }}
                        sx={{ "& fieldset": { border: "none" } }}
                    />
                    <IconButton
                        onClick={handleSend}
                        sx={{
                            flexShrink: 0,
                            width: 40, height: 40,
                            backgroundColor: PASTEL.primary,
                            color: "#fff",
                            "&:hover": { backgroundColor: PASTEL.primaryDark },
                        }}
                    >
                        <SendIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
}
